import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Box,
  Typography,
  TableFooter,
  TablePagination
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const SalesTable = ({ rows, summary, themeColors }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const displayRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <TableContainer
  component={Paper}
  sx={{
    maxHeight: "65vh",
    border: 2,        // 1px border
    borderColor: "divider", // optional: MUI divider color
    borderRadius: 1,  // optional: rounded corners
  }}
>
      <Table stickyHeader>
        <TableHead sx={{ backgroundColor: themeColors.blueAccent[700] }}>
          <TableRow>
            <TableCell />
            <TableCell>Sales Date</TableCell>
            <TableCell>Salesman</TableCell>
            <TableCell>Warehouse</TableCell>
            <TableCell>Total Quantity</TableCell>
            <TableCell>Total Price</TableCell>
            <TableCell>Paid Amount</TableCell>
            <TableCell>Credit</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {displayRows.map((row) => (
            <SalesRow key={row.id} row={row} themeColors={themeColors} />
          ))}
        </TableBody>

        {/* Totals Row */}
        <TableFooter>
          <TableRow sx={{ backgroundColor: themeColors.primary[400] }}>
            <TableCell colSpan={4} align="right" sx={{ fontWeight: "bold" }}>
              Totals:
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {summary.totalItemsSold || 0}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {Number(summary.totalSales || 0).toLocaleString()}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {Number(summary.totalPaid || 0).toLocaleString()}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {Number(summary.totalCredit || 0).toLocaleString()}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </TableContainer>
  );
};

const SalesRow = ({ row, themeColors }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover sx={{ cursor: "pointer" }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>

        <TableCell>{dayjs.utc(row.date).format("YYYY-MM-DD")}</TableCell>
        <TableCell>{row.salesman}</TableCell>
        <TableCell>{row.warehouse}</TableCell>
        <TableCell>{row.saleQty}</TableCell>
        <TableCell>{row.totalPrice}</TableCell>
        <TableCell>{row.paidAmount}</TableCell>
        <TableCell>{row.credit ? "Yes" : "No"}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Items
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item Name</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Unit Price</TableCell>
                    <TableCell>Total Price</TableCell>
                    <TableCell>Bonus Qty</TableCell>
                    <TableCell>Bonus Amount</TableCell>
                    <TableCell>Sponsor Qty</TableCell>
                    <TableCell>Sponsor Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.SalesItems?.map((item, idx) => (
                    <TableRow key={idx} sx={{ "&:last-child td": { border: 0 } }}>
                      <TableCell>{item.Item?.name || "-"}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{Number(item.unitPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                      <TableCell>{Number(item.totalPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                      <TableCell>{item.bonus || 0}</TableCell>
                      <TableCell>{Number(item.bonusAmount || 0).toFixed(2)}</TableCell>
                      <TableCell>{item.sponsor || 0}</TableCell>
                      <TableCell>{Number(item.sponsorAmount || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default SalesTable;
